<script lang="ts">
	import { browser } from '$app/environment';
	import { page } from '$app/state';
	import SmartFooter from '$lib/components/layout/SmartFooter.svelte';
	import type { Market } from '$lib/types/market';
	import type { MarketAppearance } from '$lib/types/marketAppearance';
	import '$styles/components/index.css';
	import '$styles/modern.css';
	import { onMount } from 'svelte';

	let { title, market, children } = $props<{
		title: string;
		market: Market;
		children: () => import('svelte').Snippet;
	}>();

	let appearance = $state<MarketAppearance | null>(null);

	// Fetch appearance settings
	onMount(async () => {
		if (market?.id) {
			try {
				const response = await fetch(`/api/markets/${market.id}/appearance/public`);
				if (response.ok) {
					const data = await response.json();
					appearance = data.appearance;
				}
			} catch (error) {
				console.error('Failed to load appearance settings:', error);
			}
		}
	});

	// Apply background image styles
	$effect(() => {
		if (!browser || !appearance?.backgroundImageFileName || !market?.id) {
			return;
		}

		// Use the background image route that serves from storage
		const backgroundUrl = `/market/background/${market.id}/optimized/${appearance.backgroundImageFileName}`;

		// Apply background to body using individual properties to avoid CSS accumulation
		document.body.style.backgroundImage = `url('${backgroundUrl}')`;
		document.body.style.backgroundRepeat = appearance.backgroundRepeat || 'repeat';
		document.body.style.backgroundPosition = appearance.backgroundPosition || '0 0';
		document.body.style.backgroundAttachment = appearance.backgroundAttachment || 'scroll';
		document.body.style.backgroundSize = appearance.backgroundSize || 'auto';

		// Create a style tag to ensure our styles override the CSS !important rules
		const styleId = 'modern-layout-background-override';

		// Use requestAnimationFrame to ensure DOM is stable
		requestAnimationFrame(() => {
			let styleTag = document.getElementById(styleId) as HTMLStyleElement;
			if (!styleTag && document.head) {
				styleTag = document.createElement('style');
				styleTag.id = styleId;
				try {
					document.head.appendChild(styleTag);
				} catch (e) {
					console.warn('Could not append style tag:', e);
					return;
				}
			}

			if (styleTag) {
				styleTag.textContent = `
			body.has-background-image .modern-layout,
			body.has-background-image .modern-layout .page-header,
			body.has-background-image .modern-layout .page-main,
			body.has-background-image .modern-layout .page-footer,
			body.has-background-image .modern-header,
			body.has-background-image .modern-header .header-banner-container,
			body.has-background-image .main-content {
				background: transparent !important;
				background-color: transparent !important;
			}
		`;
			}
		});

		// Add class to body to trigger the styles
		document.body.classList.add('has-background-image');

		// Make modern-layout background transparent when background image exists
		// We need to use !important to override the CSS !important rule
		const modernLayoutElement = document.querySelector('.modern-layout') as HTMLElement;
		if (modernLayoutElement) {
			modernLayoutElement.style.setProperty('background', 'transparent', 'important');
			modernLayoutElement.style.setProperty('background-color', 'transparent', 'important');
		}

		// Make other elements transparent too
		const elementsToMakeTransparent = [
			'.modern-layout .page-header',
			'.modern-layout .page-main',
			'.modern-layout .page-footer',
			'.modern-header',
			'.modern-header .header-banner-container',
			'.main-content',
		];

		elementsToMakeTransparent.forEach((selector) => {
			const element = document.querySelector(selector) as HTMLElement;
			if (element) {
				element.style.setProperty('background', 'transparent', 'important');
				element.style.setProperty('background-color', 'transparent', 'important');
			}
		});

		// Cleanup
		return () => {
			document.body.style.backgroundImage = '';
			document.body.style.backgroundRepeat = '';
			document.body.style.backgroundPosition = '';
			document.body.style.backgroundAttachment = '';
			document.body.style.backgroundSize = '';

			// Remove the class and style tag
			try {
				document.body.classList.remove('has-background-image');
				const styleToRemove = document.getElementById(styleId);
				if (styleToRemove && styleToRemove.parentNode) {
					styleToRemove.parentNode.removeChild(styleToRemove);
				}
			} catch {
				// Ignore cleanup errors
			}

			// Reset modern layout background
			if (modernLayoutElement) {
				modernLayoutElement.style.removeProperty('background');
				modernLayoutElement.style.removeProperty('background-color');
			}

			// Reset other elements
			elementsToMakeTransparent.forEach((selector) => {
				const element = document.querySelector(selector) as HTMLElement;
				if (element) {
					element.style.removeProperty('background');
					element.style.removeProperty('background-color');
				}
			});
		};
	});

	// Initialize Google Analytics in onMount to avoid hydration issues
	onMount(() => {
		// Check if GA script already exists
		const existingGa = document.querySelector('script[src*="google-analytics.com/ga.js"]');
		if (existingGa) return;

		// Initialize Google Analytics
		const _gaq = window._gaq || [];
		_gaq.push(['_setAccount', 'UA-85339-4']);
		_gaq.push(['_trackPageview']);

		if (market?.analytics_code) {
			_gaq.push(['t2._setAccount', market.analytics_code]);
			_gaq.push(['t2._trackPageview']);
		}

		const ga = document.createElement('script');
		ga.type = 'text/javascript';
		ga.async = true;
		ga.src = 'https://www.google-analytics.com/ga.js';
		ga.id = 'google-analytics-script';

		// Use requestAnimationFrame to ensure DOM is ready
		requestAnimationFrame(() => {
			const head = document.head;
			if (head && !document.getElementById('google-analytics-script')) {
				head.appendChild(ga);
			}
		});

		// Cleanup
		return () => {
			const gaScript = document.getElementById('google-analytics-script');
			if (gaScript && gaScript.parentNode) {
				gaScript.parentNode.removeChild(gaScript);
			}
		};
	});

	// Handle theme CSS in onMount to avoid hydration issues
	onMount(() => {
		if (!market?.theme || market.theme === 'None' || market.theme === 'none') {
			return;
		}

		if (page.url.pathname.startsWith('/design-showcase')) {
			return;
		}

		// Use requestAnimationFrame to ensure DOM is stable
		requestAnimationFrame(() => {
			// Remove any existing theme CSS first
			const existingTheme = document.getElementById('market-theme-css');
			if (existingTheme && existingTheme.parentNode) {
				try {
					existingTheme.parentNode.removeChild(existingTheme);
				} catch (e) {
					console.warn('Could not remove existing theme:', e);
				}
			}

			// Load theme CSS dynamically from static directory
			const themeLink = document.createElement('link');
			themeLink.rel = 'stylesheet';
			themeLink.href = `/themes/${market.theme}/theme.css`;
			themeLink.id = 'market-theme-css';

			if (document.head) {
				try {
					document.head.appendChild(themeLink);
				} catch (e) {
					console.warn('Could not append theme link:', e);
				}
			}
		});

		// Cleanup function
		return () => {
			const themeElement = document.getElementById('market-theme-css');
			if (themeElement && themeElement.parentNode) {
				try {
					themeElement.parentNode.removeChild(themeElement);
				} catch {
					// Ignore cleanup errors
				}
			}
		};
	});
</script>

<svelte:head>
	<title>{title}</title>
	<meta
		name="keywords"
		content="locally grown,locally grown food,local food,farmers market,farmers markets"
	/>
	<meta
		name="description"
		content="Locally grown food from local growers. LocallyGrown.net takes the best things about traditional farmers markets, CSAs, and buying clubs and wraps them all together in an online system that's easy for both the grower and the consumer. Find a locally grown market near you, and if there isn't one, start one!"
	/>
	<link rel="icon" href="/favicon.ico" type="image/x-icon" />
	<link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
</svelte:head>

<div class="modern-layout" id="content">
	{@render children()}
	<SmartFooter {market} />
</div>
